import type { ChangeEvent, FormEvent, JSX, SyntheticEvent } from 'react'

import { AuthFormBoxRowGaps } from "/app/libraries/arcana/src/blocks/AuthFormBox/index.ts"
import { AuthFormBoxTemplate } from "/app/libraries/arcana/src/blocks/AuthFormBox/Templates/index.ts"
import { InlineBackAngleWithTitle, PrimarySubmitButton } from "/app/libraries/arcana/src/atoms/Button/Templates/index.ts"
import { LoginPasswordInputBlock } from "/app/libraries/arcana/src/blocks/InputBlock/Templates/index.ts"
import { TextBoxHeadline2 } from "/app/libraries/arcana/src/atoms/TextBox/Templates/index.ts"

import { useForm } from 'effector-forms'
import { useUnit } from 'effector-react'
import { useTranslation } from 'react-i18next'
import { model as router } from '~/core/router'
import { $passwordI18n, FROM } from '~/features/password-security'
import { AppTitle } from '~/shared/ui/components/AppTitle'
import { AuthLayout } from '~/widgets/auth'
import { useErrorMessage } from '~/widgets/Popups/MessagePortal'
import { $locked, $message, newPasswordForm, setNewPasswordFx } from './model'

export function Title(): JSX.Element {
  return <AppTitle t={['forgot_password', 'new_password']} />
}

NewPasswordPage.displayName = 'NewPasswordPage'
export function NewPasswordPage(): JSX.Element {
  const { t } = useTranslation()

  const pending = useUnit(setNewPasswordFx.pending)
  const message = useUnit($message)
  const passwordTranslations = useUnit($passwordI18n)(FROM.ACCOUNT_INITIATE)
  const locked = useUnit($locked)
  const { submit, fields, eachValid, hasError, errorText } =
    useForm(newPasswordForm)

  const onSubmit = (e: FormEvent) => {
    e.preventDefault()
    submit()
  }

  const onGoBack = (e: SyntheticEvent) => {
    e.preventDefault()
    e.stopPropagation()
    router.navigateBack()
  }

  useErrorMessage(message, 'NewPasswordPage')

  return (
    <AuthLayout>
      <AuthFormBoxTemplate rowGap={AuthFormBoxRowGaps.SIZE_30}>
        <form onSubmit={onSubmit} autoComplete='off' noValidate>
          <a href='/forgot-password/verify' onClick={onGoBack}>
            {/* FIXME: button inside anchor is not valid HTML */}
            <InlineBackAngleWithTitle title={t('back')} />
          </a>
          <TextBoxHeadline2 title={t('change_password')} />
          <LoginPasswordInputBlock
            value={fields.password.value}
            placeholder={t('enter_new_password')}
            label={t('new_password')}
            disabled={pending || locked}
            onChange={(e: ChangeEvent<HTMLInputElement>) =>
              fields.password.onChange(e.target.value)
            }
            onBlur={() => fields.password.onBlur()}
            errorMessage={
              hasError('password')
                ? errorText('password', passwordTranslations)
                : ''
            }
            autoComplete='new-password'
          />
          <LoginPasswordInputBlock
            value={fields.confirm.value}
            placeholder={t('confirm_password')}
            label={t('confirm_password')}
            disabled={pending || locked}
            onChange={(e: ChangeEvent<HTMLInputElement>) =>
              fields.confirm.onChange(e.target.value)
            }
            onBlur={() => fields.confirm.onBlur()}
            errorMessage={
              hasError('confirm')
                ? errorText('confirm', {
                    required: t('confirm_password_required_error'),
                    equalTo: t('confirm_password_match_error'),
                  })
                : ''
            }
            autoComplete='new-password'
          />
          <PrimarySubmitButton
            title={t('verify_continue')}
            disabled={pending || !eachValid || locked}
            withLoader
            isLoading={pending || locked}
          />
        </form>
      </AuthFormBoxTemplate>
    </AuthLayout>
  )
}
